/**
 * `/poste/code` — the forced PIN change.
 *
 * A syndic sets a gardien's first PIN from the console, so the issued one is
 * known to at least two people. `gateDevice.setPin` marks it
 * `mustChangePin`, and the desk is held here until the gardien has replaced
 * it with something only they know.
 *
 * Both fields are ordinary inputs rather than the keypad used on
 * `/poste/acces`: this screen is reached once, on purpose, by someone who is
 * already sitting down — and two separate values need two separate targets.
 */
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";

import { Button } from "@gately/ui/button";
import { Input } from "@gately/ui/input";
import { Label } from "@gately/ui/label";

import {
  StationBrand,
  StationError,
  StationLead,
  StationScreen,
  StationTitle,
} from "~/components/station/station-screen";
import { orpc, orpcClient } from "~/lib/orpc";
import { setMustChangePin } from "~/lib/station/device-token";
import { PIN_MAX_LENGTH, PIN_MIN_LENGTH } from "~/lib/station/shift";

export const Route = createFileRoute("/poste/code")({
  component: ChangePinPage,
});

const digitsOnly = (value: string) =>
  value.replace(/\D/g, "").slice(0, PIN_MAX_LENGTH);

function ChangePinPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [pin, setPin] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = () => {
    if (pin.length < PIN_MIN_LENGTH) return;
    if (pin !== confirm) {
      setError("Les deux codes ne correspondent pas.");
      return;
    }
    setBusy(true);
    setError(null);
    void (async () => {
      try {
        await orpcClient.gateDevice.changePin({ pin });
        // The flag is local (no query exposes `member.mustChangePin`), so it is
        // cleared here — the server already cleared its own copy.
        setMustChangePin(false);
        // Removed rather than invalidated: `/poste`'s gate reads this through
        // `ensureQueryData`, which happily serves a stale entry, and the
        // decision must be made against the server's state as of now.
        queryClient.removeQueries({ queryKey: orpc.shift.current.key() });
        await navigate({ to: "/poste", replace: true });
      } catch (e) {
        setBusy(false);
        setError(
          (e as { message?: string }).message ??
            "Impossible de changer le code.",
        );
      }
    })();
  };

  return (
    <StationScreen>
      <StationBrand />

      <div className="mt-10">
        <StationTitle>Choisissez votre code PIN.</StationTitle>
        <StationLead>
          Ce poste est partagé. Remplacez le code qui vous a été communiqué par
          un code que vous seul connaissez.
        </StationLead>
      </div>

      <form
        className="mt-8 space-y-5"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <div className="space-y-2">
          <Label htmlFor="station-pin">Nouveau code</Label>
          <Input
            id="station-pin"
            type="password"
            inputMode="numeric"
            autoComplete="new-password"
            autoFocus
            placeholder="••••"
            className="h-14 text-center font-mono text-2xl tracking-[0.3em]"
            value={pin}
            onChange={(e) => setPin(digitsOnly(e.target.value))}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="station-pin-confirm">Confirmer le code</Label>
          <Input
            id="station-pin-confirm"
            type="password"
            inputMode="numeric"
            autoComplete="new-password"
            placeholder="••••"
            className="h-14 text-center font-mono text-2xl tracking-[0.3em]"
            value={confirm}
            onChange={(e) => setConfirm(digitsOnly(e.target.value))}
          />
        </div>

        <p className="text-muted-foreground text-[0.8125rem]">
          {PIN_MIN_LENGTH} à {PIN_MAX_LENGTH} chiffres.
        </p>

        {error ? <StationError>{error}</StationError> : null}

        <Button
          type="submit"
          className="h-14 w-full text-base"
          disabled={busy || pin.length < PIN_MIN_LENGTH}
        >
          {busy ? "Enregistrement…" : "Enregistrer et prendre le service"}
        </Button>
      </form>
    </StationScreen>
  );
}
